import {
  AggressorFlagEnum,
  ICON_SIZES,
  Icon,
  IconName,
  IndicatorBadge,
  InlineFormattedNumber,
  NumberVariants,
  Popover,
  SideEnum,
  Text,
  usePopoverState,
  type OrderIntent,
  type Security,
} from '@talos/kyoko';
import Big, { type BigSource } from 'big.js';
import { memo, useCallback, useMemo, type MouseEvent } from 'react';
import { useIsPricingModeBPS } from '../../hooks/useIsPricingModeBPS';
import type { OwnOrderResult } from '../../hooks/useOwnOrders';
import { OwnOrderRows } from '../Ladder/OwnOrders';
import { Cell, Indicator, OwnOrderIndicator, Row } from './styles';
import type { OrderBookSnapshotLevel } from './types';
import { getLevelPrice } from './utils';

interface OrderBookLevelParams {
  security: Security;
  priceIncrement: string;
  side: SideEnum;
  total: number;
  level: OrderBookSnapshotLevel;
  /* levelIndex 0 for offers is the lowest offer, 0 for bids is the highest bid  */
  levelIndex: number;
  levelsCount: number;
  nextLevel: OrderBookSnapshotLevel | undefined;
  ownOrders: Map<string, OwnOrderResult[]> | undefined;
  spread: string;
  showCumQty: boolean;
  previewOrderSide: SideEnum | undefined;
  onMouseLeave: () => void;
  onClick: (args: { side: SideEnum; levelIndex: number; quantity: string; intent: OrderIntent; price: string }) => void;
}

export const OrderBookLevel = memo(
  ({
    security,
    priceIncrement,
    side,
    total,
    level,
    levelIndex,
    levelsCount,
    nextLevel,
    ownOrders,
    spread,
    showCumQty,
    previewOrderSide,
    onClick,
    onMouseLeave,
  }: OrderBookLevelParams) => {
    const color = side === SideEnum.Sell ? 'colorTextNegative' : 'colorTextPositive';
    const isPricingModeBPS = useIsPricingModeBPS(security);

    const popover = usePopoverState({
      trigger: 'hover',
    });

    const levelPrice = getLevelPrice(level, isPricingModeBPS) !== '0' ? getLevelPrice(level, isPricingModeBPS) : '';

    const myOrders: { price: string; top: number }[] = useMemo(() => {
      // When in BPS pricing mode, pricing is in BPS.
      // We can't compare BPS to price on submitted orders so we don't show own orders
      if (isPricingModeBPS) {
        return [];
      }
      return getMyOrders(levelPrice, nextLevel, ownOrders, priceIncrement);
    }, [levelPrice, ownOrders, nextLevel, priceIncrement, isPricingModeBPS]);

    const noTopBorder =
      (side === SideEnum.Buy && levelIndex === 0) || (side === SideEnum.Sell && levelIndex === levelsCount - 1);

    const indicatorWidth =
      total > 0 && levelPrice
        ? Big((showCumQty ? level?.CumQty : level?.Size) ?? 0)
            .div(total)
            .times(100)
            .toNumber()
        : 0;

    const handleRowClick = useCallback(
      (e: MouseEvent, intent: OrderIntent, withQty: boolean) => {
        e.preventDefault();
        const quantity = withQty ? (showCumQty ? `${Big(level.CumQty ?? '0').toNumber()}` : (level.Size ?? '')) : '';
        onClick({ side, levelIndex, quantity, intent, price: levelPrice });
      },
      [level.CumQty, level.Size, levelIndex, onClick, showCumQty, side, levelPrice]
    );

    return (
      <Row
        clickable={!!levelPrice}
        noTopBorder={noTopBorder}
        data-testid="ladder"
        side={previewOrderSide}
        onMouseLeave={onMouseLeave}
        previewActive={!!previewOrderSide}
      >
        {myOrders.map(order => (
          <Popover key={order.price} {...popover}>
            <OwnOrderIndicator data-testid="own-order-indicator" style={{ top: `calc(7px - ${order.top}%)` }}>
              <Icon color={color} icon={IconName.Play} size={ICON_SIZES.SMALL} />
            </OwnOrderIndicator>
            <OwnOrders orders={ownOrders?.get(order.price)} />
          </Popover>
        ))}
        <Cell
          data-testid="price"
          pl="spacing12"
          onClick={e => handleRowClick(e, AggressorFlagEnum.Active, true)}
          onContextMenu={e => handleRowClick(e, AggressorFlagEnum.Passive, true)}
        >
          <Text>
            <InlineFormattedNumber
              number={levelPrice}
              increment={priceIncrement}
              specification={security.PriceDisplaySpec}
              variant={side === SideEnum.Sell ? NumberVariants.Negative : NumberVariants.Positive}
              round={true}
            />
          </Text>
        </Cell>
        <Cell
          onClick={e => handleRowClick(e, AggressorFlagEnum.Active, true)}
          onContextMenu={e => handleRowClick(e, AggressorFlagEnum.Passive, true)}
        >
          <Indicator side={side} style={{ width: `${indicatorWidth}%` }} />
        </Cell>
        <Cell
          justifyContent="center"
          onClick={e => handleRowClick(e, AggressorFlagEnum.Active, true)}
          onContextMenu={e => handleRowClick(e, AggressorFlagEnum.Passive, true)}
        >
          {spread && <IndicatorBadge children={spread} />}
        </Cell>
        <Cell
          justifyContent="flex-end"
          onClick={e => handleRowClick(e, AggressorFlagEnum.Active, true)}
          onContextMenu={e => handleRowClick(e, AggressorFlagEnum.Passive, true)}
          data-testid="quantity"
        >
          {level.Size !== '0' && (
            <InlineFormattedNumber
              number={showCumQty ? level.CumQty : level.Size}
              increment={security.DefaultSizeIncrement}
              specification={security.SizeDisplaySpec}
              round={true}
            />
          )}
        </Cell>
      </Row>
    );
  }
);

function OwnOrders({ orders }: { orders: OwnOrderResult[] | undefined }) {
  if (!orders?.length) {
    return null;
  }
  return <OwnOrderRows ownOrders={orders} />;
}

function getMyOrders(
  levelPrice: BigSource,
  nextLevel: OrderBookSnapshotLevel | undefined,
  ownOrders: Map<string, OwnOrderResult[]> | undefined,
  defaultIncrement: string
) {
  if (!levelPrice || !ownOrders) {
    return [];
  }
  const levelPriceBig = Big(levelPrice);
  const nextLevelPriceBig =
    nextLevel && nextLevel.Price !== '0' ? Big(nextLevel.Price) : levelPriceBig.plus(defaultIncrement);
  return [...ownOrders.keys()].reduce((acc: any[], price) => {
    const priceBig = Big(price);
    if (priceBig.gte(levelPriceBig) && priceBig.lt(nextLevelPriceBig)) {
      const a = priceBig.minus(levelPriceBig);
      const b = nextLevelPriceBig.minus(levelPriceBig);
      acc.push({
        price,
        top: b.eq(0) ? 0 : a.div(b).times(100),
      });
    }
    return acc;
  }, []);
}
